import Color from 'color';
import styled from 'styled-components';

import { DARK_GRAY, GREIGE, YELLOW } from '../../../theme/colors';
import { column, grid, row } from '../../../theme/grid';
import { MAX_WIDTH, SIZE, mediaQuery, pixelate, unit } from '../../../theme/metrics';
import { typeBodyInitial, typeBodyMd, typeBodySm, typeHeadingLg, typeHeadingMd, typeHeadingSm, typeLabelMd } from '../../../theme/typography';

export const Container = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 100%;
	padding-top: ${unit(12.5)};
	padding-bottom: ${unit(10)};
	background-color: ${props => props.theme.background};

	${mediaQuery[SIZE.medium]} {
		padding-top: ${unit(14)};
		padding-bottom: ${unit(14)};
	}

	${mediaQuery[SIZE.large]} {
		padding-top: ${unit(23)};
		padding-bottom: ${unit(23)};
	}
`;

export const Wrapper = styled.div`
	${grid({ medium: true })}
	max-width: ${pixelate(MAX_WIDTH)};
	width: 100%;
	margin-left: auto;
	margin-right: auto;
`;

export const ContentWrapper = styled.div`
	${row({ small: true })}
`;

export const BodyWrapper = styled.div`
	${column({
	small: { span: 6, total: 6 },
	large: { span: 9, total: 12 }
})};
	margin-top: ${unit(8)};

	${mediaQuery[SIZE.medium]} {
		margin-top: ${unit(9)};
	}

	${mediaQuery[SIZE.large]} {
		margin-top: 0;
	}

	.image-description {
		${typeBodySm};
		margin-top: ${unit(1)};

		${mediaQuery[SIZE.medium]} {
			margin-top: ${unit(2)};
		}
	}

	.image-wrapper {
		${column({
	small: { span: 6, total: 6 },
	medium: { span: 9, total: 9 }
})};
		margin-top: ${unit(9)};
		margin-bottom: ${unit(9)};

		img {
			display: block;
		}

		${mediaQuery[SIZE.medium]} {
			margin-top: ${unit(7)};
			margin-bottom: ${unit(7)};
		}

		${mediaQuery[SIZE.large]} {
			margin-top: ${unit(8.75)};
			margin-bottom: ${unit(10)};
		}
	}

	blockquote {
		position: relative;

		div {
			display: inline;
		}

		strong {
			${typeBodySm};
			color: ${Color(DARK_GRAY).alpha(0.9).string()};
		}

		em {
			${typeBodySm};
			color: ${Color(GREIGE).alpha(0.9).string()};
		}

		.quote-credit-open,
		.quote-credit-close {
			${typeLabelMd};
			position: relative;
			display: inline-block;
			font-size: ${unit(8.125)};
			color: ${props => props.theme.secondary};
		}

		.quote-credit-open {
			margin-right: ${unit(2)};
			position: absolute;
			top: ${unit(5)};
			left: -${unit(6)};
			margin-right: 0;
			transform: translateY(${unit(-1)});

			${mediaQuery[SIZE.medium]} {
				position: absolute;
				top: ${unit(5)};
				left: -${unit(8)};
				margin-right: 0;
				transform: translateY(${unit(-1)});
			}

			${mediaQuery[SIZE.large]} {
				position: absolute;
				top: ${unit(6)};
				left: -${unit(11.5)};
				margin-right: 0;
				transform: translateY(${unit(-1)});
			}
		}

		.quote-credit-close {
			display: none;
			margin-left: ${unit(2)};

			${mediaQuery[SIZE.medium]} {
				display: inline;
			}

			${mediaQuery[SIZE.large]} {
				transform: translateY(${unit(-1)});
			}
		}

		.quote-credit {
			display: flex;
			flex-direction: column;
			margin-top: ${unit(2)};
			max-width: ${unit(22.5)};
		}
	}
`;

export const AuthorWrapper = styled.div`
	${column({
	small: { span: 6, total: 6 },
	medium: { span: 7, before: 1, total: 9 },
	large: { span: 3, before: 0, total: 12 }
})};
`;

export const AuthorProfile = styled.div`
	display: flex;
	align-items: center;
	padding-bottom: ${unit(2)};
	margin-bottom: ${unit(2)};
	border-bottom: 1px solid ${Color(GREIGE).alpha(0.3).string()};
`;

export const AuthorImage = styled.div`
	width: ${unit(6.25)};
	flex: 0 0 ${unit(6.25)};
	height: ${unit(6.25)};
	margin-right: ${unit(2)};
	border-radius: 50%;
	overflow: hidden;
`;

export const AuthorName = styled.div`
	${typeBodyMd};
`;

export const AuthorText = styled.div`
	${typeBodySm};
	margin-bottom: ${unit(1)};
	color: ${GREIGE};

	${mediaQuery[SIZE.large]} {
		margin-bottom: ${unit(2)};
	}
`;

export const AuthorDate = styled.div`
	${typeBodySm};
	color: ${GREIGE};
`;

export const AuthorSocial = styled.div`
	display: flex;
	margin-top: ${unit(3)};

	${mediaQuery[SIZE.large]} {
		margin-top: ${unit(5)};
	}
`;

export const SocialLink = styled.a`
	display: flex;

	&:not(:last-child) {
		margin-right: ${unit(4)};
	}
`;

export const Content = styled.div`
	flex: 1 1 100%;

	h1, h2, h3 {
		${column({
	small: { span: 6, total: 6 },
	medium: { span: 7, before: 1, total: 9 }
})};

		${typeHeadingMd}
		margin-bottom: ${unit(4)};
	}

	b, strong{
		font-weight: 700;
	}

	p{
		margin-bottom: ${unit(2)};

		&:last-child {
			margin-bottom: 0;
		}
	}

	h4, h5, h6 {
		${column({
	small: { span: 6, total: 6 },
	medium: { span: 7, before: 1, total: 9 }
})};

		${typeHeadingSm}
		margin-bottom: ${unit(2)};
	}

	ul, ol {
		/* Paragraph text content */
		${column({
	small: { span: 6, total: 6 },
	medium: { span: 7, before: 1, total: 9 }
})};
	}

	p {
		/* Paragraph text content */
		${column({
	small: { span: 6, total: 6 },
	medium: { span: 7, before: 1, total: 9 }
})};

		&:first-child {
			display: inline-block;
		}

		&:first-child:first-letter {
			${typeBodyInitial}
			float: left;
			line-height: 73px;
			margin-right: ${unit(1.375)};
		}
	}

	a{
		text-decoration: underline;
		text-underline-offset: 0.2em;
		text-decoration-thickness: 1px;
  	text-decoration-color: ${props => props.theme.secondary};
	}

	blockquote {
		/* Paragraph text content */
		${column({
	small: { span: 5, before: 1, total: 6 },
	medium: { span: 8, before: 1, total: 9 },
	xlarge: { span: 10, total: 9 }
})};
		${typeHeadingLg}
		margin-top: ${unit(11)};
		margin-bottom: ${unit(10)};

		p {
			/* clear column() */
			margin-left: 0 !important;
			margin-right: 0 !important;
			padding-left: 0 !important;
			padding-right: 0 !important;
			flex: auto !important;
			width: auto !important;
			top: auto !important;
			display: inline !important;
		}

		${mediaQuery[SIZE.medium]} {
			margin-top: ${unit(7)};
			margin-bottom: ${unit(8)};
		}

		${mediaQuery[SIZE.large]} {
			float: right;
			margin-top: ${unit(11)};
			margin-bottom: ${unit(10)};
		}
	}

	ul {
		margin-top: ${unit(5)};
		margin-bottom: ${unit(5)};

		li {
			position: relative;
			margin-left: ${unit(5.5)};
			margin-bottom: ${unit(2)};

			&:before {
				position: absolute;
				top: ${unit(1)};
				left: -${unit(5.5)};
				content: "";
				height: ${unit(1)};
				width: ${unit(1)};
				background-color: ${YELLOW};
				border-radius: 50%;
			}

			ul {
				/* nested list */
				margin-top: ${unit(2)};
			}
		}
	}

	ol {
		margin-top: ${unit(5)};
		margin-bottom: ${unit(5)};
		padding-left: ${unit(3.25)};


		li {
			list-style-type: decimal;
			list-style-position: outside;
			margin-bottom: ${unit(2)};
			padding-left: ${unit(3.5)};
		}
	}

	${mediaQuery[SIZE.medium]} {
		ul {
			li {
				margin-left: ${unit(10)};
				
				&:before {
					left: -${unit(6)};
				}
			}
		}

		ol {
			padding-left: ${unit(8)};

			li {
				padding-left: ${unit(4.125)};
			}
		}
	}

	img {
		width: 100%;
	}
`;

export const Introduction = styled.div`
	${column({
	small: { span: 6, total: 6 },
	medium: { span: 7, before: 1, total: 9 }
})};
	${typeHeadingMd};
	margin-bottom: ${unit(7)};

	${mediaQuery[SIZE.medium]} {
		margin-bottom: ${unit(5)};
	}
`;

export const Label = styled.div`
	${column({
	small: { span: 6, total: 6 },
	medium: { span: 7, before: 1, total: 9 }
})};
	${typeLabelMd};
	color: ${GREIGE};
	margin-bottom: ${unit(2)};

	${mediaQuery[SIZE.medium]} {
		margin-bottom: ${unit(4)};
	}
`;

export const DownloadContainer = styled.div`
	${column({
	small: { span: 6, total: 6 },
	medium: { span: 7, before: 1, total: 9 }
})};
	display: flex;
	margin-top: ${unit(7)};

	${mediaQuery[SIZE.medium]} {
		margin-top: ${unit(10)};
	}
`;
